/**
 * ChangeProductDrawer — 2026-05-01 escape hatch for when a CofA was
 * matched to the wrong SPI product at upload time.
 *
 * Used by:
 *   - PipelineDrawer (in-flight CofAs in /cofa-docs awaiting-approval)
 *   - CofaPreviewDrawer (completed CofAs in the /cofa-docs documents tab)
 *
 * Both consumers pass: lot_id + current product + vendor info so the
 * picker can pre-filter to the vendor's known products. The mutation
 * hits POST /api/procurement/cofa/lot/{lot_id}/reassign-product which
 * updates Odoo stock.lot.product_id, all matching pipeline rows, and
 * posts chatter on both old + new product records.
 *
 * 2026-10-16: a product the vendor CofA or the vendor's open PO disagrees
 * with comes back as a 409 conflict. The drawer shows why and asks before
 * reassigning anyway (lot FPD-260904-50N went Penta Green -> Penta5 with
 * no warning).
 */
import { useMemo, useState } from 'react'
import { Button, Drawer } from '@/components/ui'
import {
  useReassignCofaProduct,
  useVendorProducts,
  vendorCofaConflictOf,
  type VendorProduct,
} from '../api/queries'

interface ChangeProductDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  lotId: number
  lotName: string
  vendorId: number | null
  vendorName: string | null
  currentProductId: number | null
  currentProductName: string | null
  onSuccess?: (newProductId: number, newProductName: string) => void
}

export function ChangeProductDrawer({
  open,
  onOpenChange,
  lotId,
  lotName,
  vendorId,
  vendorName,
  currentProductId,
  currentProductName,
  onSuccess,
}: ChangeProductDrawerProps) {
  const [search, setSearch] = useState('')
  const [pickedId, setPickedId] = useState<number | null>(null)
  const [reason, setReason] = useState('')

  const { data, isLoading, error } = useVendorProducts(vendorId, search)
  const reassign = useReassignCofaProduct()

  // Combine vendor's preferred + other matches, dedupe by id, exclude
  // the currently-attached product (no-op).
  const candidates = useMemo<VendorProduct[]>(() => {
    if (!data) return []
    const seen = new Set<number>()
    const out: VendorProduct[] = []
    for (const p of [...data.vendor_products, ...data.other_products]) {
      if (seen.has(p.id) || p.id === currentProductId) continue
      seen.add(p.id)
      out.push(p)
    }
    return out
  }, [data, currentProductId])

  const conflict = vendorCofaConflictOf(reassign.error)

  const pick = (id: number) => {
    setPickedId(id)
    reassign.reset()
  }

  const handleSave = async () => {
    if (pickedId == null) return
    let result
    try {
      result = await reassign.mutateAsync({
        lot_id: lotId,
        product_id: pickedId,
        reason: reason.trim() || null,
        acknowledge_conflict: conflict != null,
      })
    } catch {
      return // shown below: the conflict panel or the error line
    }
    onSuccess?.(result.product_id, result.product_name)
    setPickedId(null)
    setReason('')
    setSearch('')
    onOpenChange(false)
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setPickedId(null)
          setReason('')
          setSearch('')
        }
        onOpenChange(o)
      }}
      title="Change Product"
      size="standard"
    >
      <div className="flex flex-col gap-4 p-6">
        <section className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-sunk p-3">
          <h3 className="text-role-section-label">Current assignment</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
            <dt className="text-role-metadata">Lot</dt>
            <dd className="text-role-data-secondary">{lotName}</dd>
            <dt className="text-role-metadata">Vendor</dt>
            <dd className="text-role-data-secondary">
              {vendorName ?? '—'}
            </dd>
            <dt className="text-role-metadata">Current product</dt>
            <dd className="text-role-data-secondary">
              {currentProductName ?? '—'}
            </dd>
          </dl>
        </section>

        <section className="flex flex-col gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-role-metadata">Search SPI products</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type to search by product name or SKU…"
              className="border border-border-subtle bg-surface p-2 text-sm outline-none focus:border-accent"
              aria-label="Search products"
            />
          </label>

          {vendorId == null ? (
            <p role="status" className="text-role-metadata">
              No vendor on this row — search results will be limited.
            </p>
          ) : null}

          {isLoading ? (
            <p className="text-role-metadata">Loading vendor products…</p>
          ) : error ? (
            <p className="text-role-metadata text-feedback-danger">
              Could not load products: {error instanceof Error ? error.message : 'unknown error'}
            </p>
          ) : candidates.length === 0 ? (
            <p className="text-role-metadata">
              {search.trim()
                ? 'No products match. Try a different search.'
                : "No other products on this vendor's catalog. Type to search the full catalog."}
            </p>
          ) : (
            <ul
              role="listbox"
              aria-label="Pick a product"
              className="flex max-h-72 flex-col gap-1 overflow-y-auto rounded-md border border-border-subtle bg-surface p-1"
            >
              {candidates.map((p) => {
                const isPicked = pickedId === p.id
                const isPreferred =
                  data?.vendor_products.some((vp) => vp.id === p.id) ?? false
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isPicked}
                      onClick={() => pick(p.id)}
                      className={
                        'flex w-full flex-col items-start gap-0.5 rounded-sm border p-2 text-left text-sm ' +
                        (isPicked
                          ? 'border-accent bg-accent-bg'
                          : 'border-transparent hover:bg-surface-sunk')
                      }
                    >
                      <span className="text-role-data-primary">{p.name}</span>
                      <span className="text-role-metadata">
                        {p.default_code || '—'}
                        {isPreferred ? ' · vendor catalog' : ''}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        <label className="flex flex-col gap-1">
          <span className="text-role-metadata">Reason (optional)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Posted to chatter on both products."
            className="border border-border-subtle bg-surface p-2 text-sm outline-none focus:border-accent"
          />
        </label>

        {conflict ? (
          <div
            role="alert"
            className="flex flex-col gap-1 rounded-md border border-warning-border bg-warning-bg p-3 text-role-data-secondary"
          >
            <strong className="text-warning-text">This doesn't match the vendor CofA</strong>
            <span>{conflict.reason}</span>
            <span className="text-role-metadata">
              Reassigning anyway keeps this CofA flagged until regulatory verifies it in CofA Management.
            </span>
          </div>
        ) : reassign.isError ? (
          <p
            role="alert"
            className="rounded-md border border-feedback-danger-border bg-feedback-danger-bg p-2 text-role-data-secondary"
          >
            {reassign.error instanceof Error
              ? reassign.error.message
              : 'Reassign failed.'}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={reassign.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={pickedId == null || reassign.isPending}
          >
            {reassign.isPending ? 'Saving…' : conflict ? 'Reassign anyway' : 'Reassign'}
          </Button>
        </div>
      </div>
    </Drawer>
  )
}
